import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://superbuyspread.hair';
const pagePath = '/article/superbuy-spreadsheet-template-guide';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline:
    'Superbuy Spreadsheet Template Library: Find the Perfect Template for Every Shopping Style',
  description:
    'Explore our complete collection of free and premium Superbuy Spreadsheet templates — from basic order trackers to advanced profit calculators built for resellers and dropshipping entrepreneurs.',
  author: {
    '@type': 'Organization',
    name: 'SuperbuySpread.hair Editorial Team',
    url: siteUrl,
  },
  publisher: {
    '@type': 'Organization',
    name: 'SuperbuySpread.hair',
    url: siteUrl,
  },
  datePublished: '2026-07-13',
  dateModified: '2026-07-13',
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${siteUrl}${pagePath}`,
  },
  inLanguage: 'en',
};

export default function TemplateGuidePage() {
  return (
    <>
      <SchemaOrg data={articleSchema} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28 pb-10 md:pb-14">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <nav className="mb-6" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-xs md:text-sm text-foreground-400">
                <li>
                  <Link to="/" className="hover:text-primary-500 transition-colors duration-150">
                    Home
                  </Link>
                </li>
                <li className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </li>
                <li className="text-foreground-600 font-medium">Template Library</li>
              </ol>
            </nav>

            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                  Templates
                </span>
                <span className="text-xs text-foreground-400">11 min read</span>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
                Superbuy Spreadsheet Template Library: Find the Perfect Template for Every Shopping Style
              </h1>
              <p className="text-foreground-500 text-sm md:text-base leading-relaxed">
                Explore our complete collection of free and premium Superbuy Spreadsheet templates — from basic order trackers to advanced profit calculators built for resellers and dropshipping entrepreneurs.
              </p>
            </header>

            <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20overhead%20flat%20lay%20of%20neatly%20organized%20printed%20spreadsheet%20templates%20with%20a%20modern%20laptop%20and%20stationery%20on%20a%20cream%20colored%20wooden%20desk%20surface%20soft%20natural%20daylight%20from%20above%20editorial%20productivity%20aesthetic%20warm%20earth%20tones%20clean%20orderly%20composition%20professional%20workspace%20atmosphere&width=1200&height=600&seq=article-templates-hero-03&orientation=landscape"
                alt="Superbuy Spreadsheet template library collection"
                title="Superbuy Spreadsheet Template Library - Free and Premium Templates"
                className="w-full h-full object-cover object-center"
              />
            </div>

            <div className="prose-custom text-foreground-800 text-sm md:text-base leading-relaxed space-y-6">
              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Why Templates Matter More Than You Think
                </h2>
                <p>
                  A blank spreadsheet is intimidating. A well-designed template is empowering. The difference between building a tracking system from scratch and starting with a <strong>Superbuy Spreadsheet template</strong> is the difference between spending three hours configuring columns and formulas versus spending three minutes entering your first product. Our template library removes the setup barrier entirely — every template comes pre-loaded with the formulas, conditional formatting, and layout that experienced international shoppers rely on.
                </p>
                <p>
                  More importantly, templates encode <em>best practices</em>. When you use a Superbuy Spreadsheet template, you are not just getting a file — you are getting years of accumulated knowledge about which shipping variables matter, how to structure product comparisons, and which budget metrics actually influence smarter purchasing decisions.
                </p>
                <p>
                  New to all this? Start with our{' '}
                  <Link to="/article/superbuy-spreadsheet-for-beginners" className="text-primary-500 hover:text-primary-600 font-medium underline underline-offset-2">
                    beginner's guide to Superbuy Spreadsheet
                  </Link>{' '}
                  or browse categories on{' '}
                  <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium underline underline-offset-2">
                    the homepage
                  </Link>.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Free Template Collection: Everything You Need to Start
                </h2>

                <div className="space-y-5 my-6">
                  <div className="rounded-xl bg-background-100 p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-list-3-line text-primary-600 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground-900 text-sm">Starter Product Tracker</h4>
                        <span className="text-xs text-primary-500 font-medium">Free</span>
                      </div>
                    </div>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      The entry-level template for casual shoppers. Includes product database with auto-sorting, price column with currency conversion formulas, and a simple order status tracker. Perfect for managing 10-30 active products across 2-3 marketplaces.
                    </p>
                  </div>

                  <div className="rounded-xl bg-background-100 p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-ship-line text-primary-600 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground-900 text-sm">Shipping Cost Calculator</h4>
                        <span className="text-xs text-primary-500 font-medium">Free</span>
                      </div>
                    </div>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      A standalone shipping module with dimensional weight calculator, multi-carrier rate comparison (EMS, DHL, FedEx, Sea Freight), and a consolidation savings estimator. Integrates with the product tracker to pull weights automatically.
                    </p>
                  </div>

                  <div className="rounded-xl bg-background-100 p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-pie-chart-line text-primary-600 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground-900 text-sm">Monthly Budget Dashboard</h4>
                        <span className="text-xs text-primary-500 font-medium">Free</span>
                      </div>
                    </div>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      Visual budget tracking with spending progress bars, category breakdown pie charts, and monthly-over-month comparison. Set alerts at 50%, 75%, and 90% of your budget limit.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Premium Templates for Power Users
                </h2>

                <div className="space-y-5 my-6">
                  <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-foreground-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-building-line text-foreground-700 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground-900 text-sm">Reseller Profit Calculator</h4>
                        <span className="text-xs text-foreground-400 font-medium">Premium</span>
                      </div>
                    </div>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      Built specifically for resellers who need to track profit margins per item. Calculates landed cost (product + shipping + customs + platform fees), suggested resale price based on target margin, and net profit after all expenses. Supports batch imports of 100+ products.
                    </p>
                  </div>

                  <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-foreground-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-global-line text-foreground-700 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground-900 text-sm">Dropshipping Order Manager</h4>
                        <span className="text-xs text-foreground-400 font-medium">Premium</span>
                      </div>
                    </div>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      Manages supplier relationships and customer orders in one place. Tracks supplier communication, fulfillment times, return rates, and quality scores. Customer-facing columns can be shared read-only with buyers for order transparency.
                    </p>
                  </div>

                  <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-foreground-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-funds-line text-foreground-700 text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground-900 text-sm">Multi-Currency Portfolio Tracker</h4>
                        <span className="text-xs text-foreground-400 font-medium">Premium</span>
                      </div>
                    </div>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      For shoppers who buy across currencies regularly. Live exchange rate integration (manual refresh), portfolio valuation in your home currency, and historical rate tracking to identify favorable buying windows.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Industry-Specific Templates
                </h2>

                <div className="overflow-x-auto my-6">
                  <table className="w-full text-xs md:text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-background-200">
                        <th className="text-left py-3 px-4 font-semibold text-foreground-700">Industry</th>
                        <th className="text-left py-3 px-4 font-semibold text-foreground-700">Template</th>
                        <th className="text-left py-3 px-4 font-semibold text-foreground-700">Key Feature</th>
                        <th className="text-left py-3 px-4 font-semibold text-foreground-700">Tier</th>
                      </tr>
                    </thead>
                    <tbody className="text-foreground-600">
                      <tr className="border-b border-background-100">
                        <td className="py-3 px-4 font-medium text-foreground-800">Sneaker Reselling</td>
                        <td className="py-3 px-4">Sneaker Flip Calculator</td>
                        <td className="py-3 px-4">Size-based pricing, authentication tracking</td>
                        <td className="py-3 px-4"><span className="text-xs text-primary-500 font-medium">Free</span></td>
                      </tr>
                      <tr className="border-b border-background-100">
                        <td className="py-3 px-4 font-medium text-foreground-800">Fashion / Streetwear</td>
                        <td className="py-3 px-4">Collection Manager</td>
                        <td className="py-3 px-4">Release calendar, brand catalog</td>
                        <td className="py-3 px-4"><span className="text-xs text-foreground-400 font-medium">Premium</span></td>
                      </tr>
                      <tr className="border-b border-background-100">
                        <td className="py-3 px-4 font-medium text-foreground-800">Electronics</td>
                        <td className="py-3 px-4">Gadget Import Tracker</td>
                        <td className="py-3 px-4">Voltage specs, warranty tracking, CE/FCC notes</td>
                        <td className="py-3 px-4"><span className="text-xs text-primary-500 font-medium">Free</span></td>
                      </tr>
                      <tr className="border-b border-background-100">
                        <td className="py-3 px-4 font-medium text-foreground-800">Home Decor / Furniture</td>
                        <td className="py-3 px-4">Bulk Freight Planner</td>
                        <td className="py-3 px-4">CBM calculator, sea freight LCL/FCL comparison</td>
                        <td className="py-3 px-4"><span className="text-xs text-foreground-400 font-medium">Premium</span></td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 font-medium text-foreground-800">Beauty / Cosmetics</td>
                        <td className="py-3 px-4">Batch Expiry Tracker</td>
                        <td className="py-3 px-4">Manufacturing date, shelf life alerts</td>
                        <td className="py-3 px-4"><span className="text-xs text-primary-500 font-medium">Free</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  How to Choose the Right Template
                </h2>
                <p>
                  With multiple template options available, picking the right one depends on your shopping profile. Here is a quick decision guide:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-sm text-foreground-600 my-4">
                  <li><strong>Buying 1-5 items per month?</strong> Start with the free Starter Product Tracker. It covers all your needs without overwhelming you.</li>
                  <li><strong>Reselling for profit?</strong> The Reseller Profit Calculator is essential. It tracks every cost so you know your true margin on each sale.</li>
                  <li><strong>Running a dropshipping store?</strong> The Dropshipping Order Manager was built for your workflow — supplier management, customer tracking, and fulfillment monitoring in one place.</li>
                  <li><strong>Shopping across multiple countries and currencies?</strong> Combine the Multi-Currency Portfolio Tracker with the Shipping Cost Calculator for a complete cross-border system.</li>
                </ul>
                <p>
                  For a deeper understanding of the entire system, check out our{' '}
                  <Link to="/article/superbuy-spreadsheet-complete-guide" className="text-primary-500 hover:text-primary-600 font-medium underline underline-offset-2">
                    complete guide
                  </Link>{' '}
                  and the{' '}
                  <Link to="/article/superbuy-spreadsheet-shipping-guide" className="text-primary-500 hover:text-primary-600 font-medium underline underline-offset-2">
                    shipping optimization guide
                  </Link>.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Template Customization: Make It Your Own
                </h2>
                <p>
                  Every Superbuy Spreadsheet template is designed to be a <strong>starting point, not a straitjacket</strong>. The templates use protected formula cells for critical calculations — so you cannot accidentally break the shipping estimator or profit calculator — but all data entry areas and display columns are fully customizable.
                </p>
                <p>
                  Common customizations our community members make include adding personal rating columns for sellers, creating wishlist priority tiers with custom color coding, adding notes fields for item-specific details like sizing tips or material quality, and inserting links to fit pics or review photos alongside product entries.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Template FAQ
                </h2>
                <div className="space-y-4 my-6">
                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-2">
                      Can I switch from a free template to a premium one later?
                    </h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      Yes. All templates share the same column naming conventions, so you can copy your data from a free template and paste it directly into a premium template with zero reformatting. The transition takes about two minutes.
                    </p>
                  </div>
                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-2">
                      Are the templates compatible with Apple Numbers or LibreOffice?
                    </h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      Templates are optimized for Google Sheets and Microsoft Excel. While they will open in Numbers and LibreOffice, some conditional formatting and advanced formulas may not render perfectly. Google Sheets is recommended for best results.
                    </p>
                  </div>
                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-2">
                      How often are templates updated?
                    </h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      Templates receive quarterly updates based on carrier rate changes, community feedback, and new feature requests. Premium templates include automatic notification when updates are available.
                    </p>
                  </div>
                </div>
              </section>

              <section className="mt-12 pt-8 border-t border-background-200">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  Get the Right Template for Your Shopping Style
                </h2>
                <p>
                  The right template transforms how you shop internationally. It turns scattered notes and mental math into a structured system that saves money on every purchase. Whether you are a casual browser, a dedicated collector, or a full-time reseller, there is a Superbuy Spreadsheet template built for your workflow.
                </p>
                <p>
                  Browse all product categories on{' '}
                  <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium underline underline-offset-2">
                    the homepage
                  </Link>, or visit{' '}
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-primary-500 hover:text-primary-600 font-medium underline underline-offset-2"
                  >
                    our main website
                  </a>{' '}
                  to access the full template library.
                </p>
              </section>
            </div>

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-xs text-foreground-400">Published by SuperbuySpread.hair Editorial Team</p>
                  <p className="text-xs text-foreground-400">Last updated: July 2026</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <Link
                    to="/article/superbuy-spreadsheet-complete-guide"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-foreground-200 text-foreground-600 text-xs font-semibold rounded-full hover:bg-foreground-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <span>Complete Guide</span>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </Link>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 text-xs font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line text-xs"></i>
                    </div>
                    <span>Back to Homepage</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}